import React, { useState, useRef, useEffect } from 'react';
import { Bug, Lightbulb, MessageSquare, X, Send, ChevronUp, AlertCircle, CheckCircle2, Paperclip, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useToast } from '../lib/toast';
import { supabase } from '../lib/supabase';

type FeedbackType = 'bug' | 'feature_request' | 'feedback';
type Severity = 'low' | 'medium' | 'high' | 'critical';

interface Attachment {
  name: string;
  path: string;
  type: string;
  size: number;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf', 'text/plain', 'text/csv', 'application/json', 'application/zip', 'image/bmp'];
const MAX_ATTACHMENTS = 6;

const FeedbackWidget: React.FC = () => {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('bug');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myReports, setMyReports] = useState<Array<{ id: string; title: string; status: string; type: string; admin_response: string | null; attachments?: Attachment[] }>>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const typeConfig: Record<FeedbackType, { icon: React.ElementType; label: string; color: string }> = {
    bug: { icon: Bug, label: 'Bug Report', color: 'text-rose-600' },
    feature_request: { icon: Lightbulb, label: 'Feature Request', color: 'text-amber-600' },
    feedback: { icon: MessageSquare, label: 'Feedback', color: 'text-blue-600' },
  };

  const severityConfig: Record<Severity, { label: string; color: string }> = {
    low: { label: 'Low', color: 'bg-slate-100 text-slate-600 border-slate-200' },
    medium: { label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    high: { label: 'High', color: 'bg-orange-50 text-orange-700 border-orange-200' },
    critical: { label: 'Critical', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  };

  useEffect(() => {
    if (isOpen && user) loadMyReports();
  }, [isOpen, user]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const trigger = document.getElementById('feedback-widget-trigger');
        if (trigger && trigger.contains(e.target as Node)) return;
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadMyReports = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('feedback_reports')
      .select('id, title, status, type, admin_response, attachments')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);
    if (data) setMyReports(data);
  };

  const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);

  const handleFiles = async (fileList: FileList | File[]) => {
    if (!user) {
      showToast('Please sign in to upload files', 'error');
      return;
    }
    const files = Array.from(fileList);
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      showToast(`You can attach up to ${MAX_ATTACHMENTS} files`, 'error');
      return;
    }

    setUploading(true);
    const added: Attachment[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        showToast(`${file.name} is too large (max 10 MB)`, 'error');
        continue;
      }
      const tempId = crypto.randomUUID();
      const safeName = sanitizeFileName(file.name);
      const filePath = `${user.id}/${tempId}/${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('feedback-attachments')
        .upload(filePath, file, { contentType: file.type || 'application/octet-stream' });
      if (upErr) {
        showToast(`Failed to upload ${file.name}`, 'error');
        continue;
      }
      added.push({ name: file.name, path: filePath, type: file.type || 'application/octet-stream', size: file.size });
    }
    if (added.length > 0) setAttachments(prev => [...prev, ...added]);
    setUploading(false);
  };

  const removeAttachment = async (att: Attachment) => {
    const { error } = await supabase.storage.from('feedback-attachments').remove([att.path]);
    if (error) {
      showToast('Could not remove file', 'error');
      return;
    }
    setAttachments(prev => prev.filter(a => a.path !== att.path));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const submit = async () => {
    if (!title.trim() || !description.trim()) {
      showToast('Please provide a title and description', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user_id: user?.id ?? null,
        tenant_id: tenantId ?? null,
        type,
        severity,
        title: title.trim(),
        description: description.trim(),
        page_url: window.location.hash || window.location.pathname,
        user_agent: navigator.userAgent,
        user_email: user?.email ?? null,
        attachments: attachments.length > 0 ? attachments : [],
      };
      const { error } = await supabase.from('feedback_reports').insert(payload);
      if (error) throw error;
      showToast('Report submitted. Thank you!', 'success');
      setSubmitted(true);
      setTitle('');
      setDescription('');
      setSeverity('medium');
      setType('bug');
      setAttachments([]);
      setTimeout(() => setSubmitted(false), 3000);
      loadMyReports();
    } catch {
      showToast('Failed to submit report. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors: Record<string, string> = {
    open: 'bg-blue-50 text-blue-600',
    acknowledged: 'bg-amber-50 text-amber-600',
    resolved: 'bg-emerald-50 text-emerald-600',
    closed: 'bg-slate-100 text-slate-500',
  };

  const isImage = (type: string) => type.startsWith('image/');

  return (
    <>
      <button
        id="feedback-widget-trigger"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-lg shadow-violet-500/30 hover:scale-105 transition-transform flex items-center justify-center group"
        title="Report a bug or share feedback"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] glass-card rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-fade-in"
        >
          <div className="bg-gradient-to-r from-violet-600 to-blue-600 px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-white" />
              <h3 className="text-sm font-semibold text-white">Feedback & Bug Reports</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {submitted && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg animate-fade-in">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <p className="text-sm text-emerald-700">Report submitted successfully!</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Type</label>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(typeConfig) as FeedbackType[]).map(t => {
                  const cfg = typeConfig[t];
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      className={`p-2.5 rounded-lg border text-xs font-medium flex flex-col items-center gap-1 transition-all ${
                        type === t
                          ? 'border-violet-300 bg-violet-50 ' + cfg.color
                          : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Severity</label>
              <div className="grid grid-cols-4 gap-2">
                {(Object.keys(severityConfig) as Severity[]).map(s => {
                  const cfg = severityConfig[s];
                  return (
                    <button
                      key={s}
                      onClick={() => setSeverity(s)}
                      className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                        severity === s ? cfg.color : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Brief summary of the issue..."
                maxLength={120}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 bg-white text-slate-700"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe what happened, what you expected, and any steps to reproduce..."
                maxLength={2000}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 bg-white text-slate-700 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
                Attachments <span className="text-slate-400 normal-case">({attachments.length}/{MAX_ATTACHMENTS})</span>
              </label>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                  dragOver ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/30'
                }`}
              >
                <Paperclip className="h-5 w-5 text-slate-400 mx-auto mb-1.5" />
                <p className="text-xs text-slate-500">
                  {uploading ? 'Uploading...' : 'Drag & drop or click to add screenshots, logs, or documents'}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Images, PDFs, logs — up to 10 MB each</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES.join(',')}
                  onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
                  className="hidden"
                />
              </div>

              {attachments.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                      {isImage(att.type) ? <ImageIcon className="h-4 w-4 text-violet-500 flex-shrink-0" /> : <Paperclip className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                      <span className="text-xs text-slate-600 truncate flex-1">{att.name}</span>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{(att.size / 1024).toFixed(0)} KB</span>
                      <button
                        onClick={e => { e.stopPropagation(); removeAttachment(att); }}
                        className="text-slate-400 hover:text-rose-500 transition-colors flex-shrink-0"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-400">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>Page location is automatically captured with your report</span>
            </div>

            <button
              onClick={submit}
              disabled={submitting || uploading || !title.trim() || !description.trim()}
              className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting...</> : <><Send className="h-4 w-4" /> Submit Report</>}
            </button>

            {myReports.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Your Recent Reports</p>
                <div className="space-y-2">
                  {myReports.map(r => (
                    <div key={r.id} className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-slate-700 truncate">{r.title}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${statusColors[r.status] ?? statusColors.open}`}>{r.status}</span>
                      </div>
                      {r.attachments && r.attachments.length > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-violet-600">
                          <Paperclip className="h-3 w-3" />
                          <span>{r.attachments.length} attachment{r.attachments.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                      {r.admin_response && (
                        <div className="mt-1.5 p-2 bg-violet-50 rounded text-[11px] text-violet-700">
                          <span className="font-medium">Admin:</span> {r.admin_response}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
